// Video Banner Manager - Global Class for Multiple Sections
// Save this as assets/video-banner.js

class VideoBanner {
  constructor(sectionId, options = {}) {
    this.sectionId = sectionId;
    this.section = document.getElementById(`videoBanner${sectionId}`);
    this.desktopVideo = document.getElementById(`backgroundVideoDesktop${sectionId}`);
    this.mobileVideo = document.getElementById(`backgroundVideoMobile${sectionId}`);
    this.videoContainer = document.getElementById(`videoContainer${sectionId}`);
    this.contentContainer = document.getElementById(`contentContainer${sectionId}`);
    this.originalBackgroundImage = this.contentContainer?.style.backgroundImage || '';


    this.playbackSpeed = options.playbackSpeed || 1.0; // Default speed = 1.0
    this.fadeOutVideo = options.fadeOutVideo !== false; // Default true;
    
    // Determine which video to use based on screen size
    this.currentVideo = this.getCurrentVideo();
    
    this.isVideoPlaying = false;
    this.hasPlayedOnce = false;
    this.fadeTimeout = null;
    
    this.init();
  }

  getCurrentVideo() {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile && this.mobileVideo) {
      // console.log('Using mobile video');
      return this.mobileVideo;
    } else if (this.desktopVideo) {
      // console.log('Using desktop video');
      return this.desktopVideo;
    }
    
    // console.log('No video found');
    return null;
  }

  init() {
    // console.log('Initializing VideoBanner for section:', this.sectionId);
    // console.log('Desktop video element:', this.desktopVideo);
    // console.log('Mobile video element:', this.mobileVideo);
    
    // Get current video and log it
    this.currentVideo = this.getCurrentVideo();
    // console.log('Current video selected:', this.currentVideo);
    
    // Check if we have a video to work with
    if (!this.currentVideo) {
      console.error(`No video available for section ${this.sectionId} - showing content immediately`);
      setTimeout(() => this.showContent(), 100);
      return;
    }
    
    // Set up intersection observer for scroll detection
    this.setupIntersectionObserver();
    
    // Set up video event listeners
    this.setupVideoEvents();
    
    // Handle responsive video switching
    this.handleResponsiveVideo();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3 // Reduced threshold to trigger earlier
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        console.log(`Section ${this.sectionId} intersection:`, {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          threshold: options.threshold
        });
        
        if (entry.isIntersecting) {
          console.log(`Section ${this.sectionId} entered viewport`);
          this.onSectionEnter();
        } else {
          console.log(`Section ${this.sectionId} left viewport`);
          this.onSectionExit();
        }
      });
    }, options);

    observer.observe(this.section);
    console.log(`Intersection observer set up for section ${this.sectionId}`);
  }

  setupVideoEvents() {
    if (!this.currentVideo) return;
    
    this.currentVideo.addEventListener('ended', () => {
      this.onVideoEnd();
    });

    this.currentVideo.addEventListener('loadeddata', () => {
      console.log(`Video ${this.sectionId} loaded and ready to play`);
    });

    this.currentVideo.addEventListener('error', (e) => {
      console.error(`Video ${this.sectionId} error:`, e);
      // Fallback to show content immediately if video fails
      this.showContent();
    });
  }

  handleResponsiveVideo() {
    // Update current video reference on screen size change
    const updateCurrentVideo = () => {
      const newVideo = this.getCurrentVideo();
      if (newVideo !== this.currentVideo) {
        // Pause old video if playing
        if (this.currentVideo && this.isVideoPlaying) {
          this.currentVideo.pause();
        }
        
        this.currentVideo = newVideo;
        this.setupVideoEvents();
        
        // If we were in the middle of playing, restart with new video
        if (this.isVideoPlaying) {
          this.isVideoPlaying = false;
          this.playVideo();
        }
      }
    };

    window.addEventListener('resize', updateCurrentVideo);
  }

  onSectionEnter() {
    console.log(`onSectionEnter called for section ${this.sectionId}`);
    console.log(`isVideoPlaying: ${this.isVideoPlaying}`);
    
    if (!this.isVideoPlaying) {
      console.log(`Starting video playback for section ${this.sectionId}`);
      this.playVideo();
    } else {
      console.log(`Video already playing for section ${this.sectionId}`);
    }
  }

  onSectionExit() {
    console.log(`onSectionExit called for section ${this.sectionId}`);
    // Pause current video when section is not visible
    if (this.isVideoPlaying && this.currentVideo) {
      console.log(`Pausing video for section ${this.sectionId}`);
      this.currentVideo.pause();
      this.isVideoPlaying = false;
    }
  }

  playVideo() {
    console.log(`playVideo called for section ${this.sectionId}`);
    console.log(`hasPlayedOnce: ${this.hasPlayedOnce}`);
    console.log(`contentContainer has active class: ${this.contentContainer.classList.contains('active')}`);
    
    // Clear any existing fade timeout
    if (this.fadeTimeout) {
      clearTimeout(this.fadeTimeout);
    }

    // If content is showing and we want to replay, fade out content first
    if (this.hasPlayedOnce && this.contentContainer.classList.contains('active')) {
      console.log(`Fading out content before replaying video for section ${this.sectionId}`);
      this.contentContainer.classList.remove('active');
      
      // Wait for content to fade out before showing video
      setTimeout(() => {
        this.startVideo();
      }, 500);
    } else {
      console.log(`Starting video immediately for section ${this.sectionId}`);
      this.startVideo();
    }
  }
  startVideo() {
    console.log('startVideo called for section:', this.sectionId);

    this.currentVideo = this.getCurrentVideo();
    if (!this.currentVideo) {
      console.error(`No video available for section ${this.sectionId} in startVideo`);
      this.showContent();
      return;
    }

    // Set playback speed based on data attribute
    this.currentVideo.playbackRate = this.playbackSpeed;

    // Ensure the video is loaded
    if (this.currentVideo.readyState === 0) {
      this.currentVideo.load(); // force reload
    }

    this.videoContainer.classList.add('active');

    try {
      setTimeout(() => {
        const playPromise = this.currentVideo.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            this.isVideoPlaying = true;
            console.log(`Video ${this.sectionId} started playing successfully`);
          }).catch(error => {
            console.error(`Video ${this.sectionId} play failed:`, error);
            this.handleAutoplayBlocked();
          });
        } else {
          this.isVideoPlaying = true;
          console.log(`Video ${this.sectionId} play method called (no promise)`);
        }
      }, 100);
    } catch (error) {
      console.error(`Error playing video ${this.sectionId}:`, error);
      this.showContent();
    }
  }


  handleAutoplayBlocked() {
    console.log(`Autoplay blocked for section ${this.sectionId}, showing content instead`);
    // Show content immediately if autoplay is blocked
    this.showContent();
    
    // Optional: Add click listener to play video on user interaction
    this.addClickToPlayFunctionality();
  }

  addClickToPlayFunctionality() {
    const playButton = document.createElement('button');
    playButton.innerHTML = '▶ Play Video';
    playButton.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
      background: rgba(0,0,0,0.8);
      color: white;
      border: 2px solid white;
      padding: 15px 30px;
      font-size: 18px;
      cursor: pointer;
      border-radius: 5px;
    `;
    
    playButton.addEventListener('click', () => {
      this.currentVideo.play().then(() => {
        this.isVideoPlaying = true;
        this.contentContainer.classList.remove('active');
        playButton.remove();
      });
    });
    
    this.section.appendChild(playButton);
  }

  onVideoEnd() {
    this.isVideoPlaying = false;
    this.hasPlayedOnce = true;
    
    if (this.fadeOutVideo){
      this.videoContainer.classList.remove('active');
      // Wait for video to fade out before showing content
      this.fadeTimeout = setTimeout(() => {
        this.showContent();
      }, 500);
    } else {
      this.showContent();
    }
    
  }

  showContent() {
    this.contentContainer.classList.add('active');
    if (!this.fadeOutVideo) {
      this.contentContainer.style.backgroundImage = 'none';
    }
  }
}

// Global Video Banner Manager
window.VideoBannerManager = {
  instances: new Map(),
  
  initSection: function(sectionId) {
    console.log('VideoBannerManager: Initializing section', sectionId);
    
    // Don't initialize if already exists
    if (this.instances.has(sectionId)) {
      console.log('Section already initialized:', sectionId);
      return;
    }
    
    // Add delay to ensure DOM is ready
    setTimeout(() => {
      const instance = new VideoBanner(sectionId);
      this.instances.set(sectionId, instance);
      console.log('Section initialized successfully:', sectionId);
    }, 200);
  },
  
  destroySection: function(sectionId) {
    if (this.instances.has(sectionId)) {
      // Clean up if needed
      this.instances.delete(sectionId);
      console.log('Section destroyed:', sectionId);
    }
  },
  
  pauseAllVideos: function() {
    this.instances.forEach((instance) => {
      if (instance.currentVideo && instance.isVideoPlaying) {
        instance.currentVideo.pause();
      }
    });
  }
};

// Handle page visibility change to pause all videos
document.addEventListener('visibilitychange', () => {
  if (document.hidden && window.VideoBannerManager) {
    window.VideoBannerManager.pauseAllVideos();
  }
});